import { Autocomplete } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo, useCallback, useMemo } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Service } from 'shell/models/service';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import debounce from 'shell/utils/debounce';
import PmiTextField from 'shell/components/shared/text-field';

interface ServiceListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (acronym?: string) => void;
  error?: boolean;
  label?: string;
  id?: string;
  required?: boolean;
}

const ServiceListComponent = ({
  disabled,
  onChange,
  required,
  value,
  error,
  label = 'Servizio',
  id = 'service-list',
}: ServiceListComponentProps): React.ReactElement => {
  const [service, getServices] = useHttpFetch<HttpResponseList<Service>, Array<ItemVM>>(
    `${__MANAGEMENT_API_URL__}/servizi`,
    {
      onBootstrap: false,
      initialData: [],
      options: {
        loading: false,
        errorCatching: true,
      },
      transformer: (response: AxiosResponse<HttpResponseList<Service>>) => {
        const { data } = response;

        return data?.payload.list.map((item) => ({
          id: item.key.codiceServizio,
          value: item.descrizione,
        })) as Array<ItemVM>;
      },
    }
  );

  const defaultProps = useMemo(() => {
    return {
      options: (service.data as Array<ItemVM>) || [],
      getOptionLabel: (option: ItemVM) => option.value,
    };
  }, [service.data]);

  const handleSearch = debounce<string>(
    useCallback(
      (searchValue) => {
        if (service.abort) {
          service.abort();
        }

        getServices({
          params: {},
          paramsSerializer: () => `filters=descrizione:${searchValue}:${Operation.LIKE}`,
        });
      },
      [getServices, service.abort]
    )
  );

  const selectedValue = defaultProps.options.find((elem) => elem.id === value);

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      value={selectedValue}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      onChange={(event, element) => {
        event.stopPropagation();
        onChange((element?.id as string) || '');
      }}
      disabled={disabled}
      onInputChange={(event, changeValue, reason) => {
        if (changeValue.length >= 3 && reason === 'input') {
          handleSearch(changeValue);
        }
      }}
      clearOnBlur={false}
      loading={service.isLoading}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );

  // const list = (service.data as Array<ItemVM>) || [];

  // const idTag = `${id}-${label}`;

  // return (
  //   <FormControl fullWidth>
  //     <InputLabel error={error} id={idTag}>
  //       {label}
  //     </InputLabel>
  //     <PmiSelect
  //       labelId={idTag}
  //       id={id}
  //       name={id}
  //       value={value}
  //       label={label}
  //       onChange={(event) => {
  //         const selectedValue = event.target.value;
  //         onChange(selectedValue);
  //       }}
  //       disabled={disabled}
  //       error={error}
  //     >
  //       <MenuItem value="">
  //         <em>Nessuno</em>
  //       </MenuItem>
  //       {list.map((item: ItemVM) => (
  //         <MenuItem key={item.id} value={item.id}>
  //           {item.id} - {item.value}
  //         </MenuItem>
  //       ))}
  //     </PmiSelect>
  //   </FormControl>
  // );
};

ServiceListComponent.displayName = 'Service List component';

const ServiceList = memo(ServiceListComponent);

export default ServiceList as typeof ServiceListComponent;
